import React, { useRef, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { FullscreenOutlined, FullscreenExitOutlined, WechatOutlined } from '@ant-design/icons';
import { Button, message as antMessage } from 'antd';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import { MessageContentProps, UIMessage } from '../../../types/chat';
import { Patient, PatientInfoForm } from '../../consultation_form/types';
import PatientList from '../../consultation_form/PatientList';
import Fill from '../../consultation_form/Fill';
import Info from '../../consultation_form/Info';
import Succeed from '../../consultation_form/Succeed';
import KnowledgeItemSummary from '../../knowledge/KnowledgeItemSummary';
import { savePatientInfo } from '../../../services/consultationService';

// 动画效果
const blink = keyframes`
    0% { opacity: .2; }
    20% { opacity: 1; }
    100% { opacity: .2; }
`;

// 加载动画组件
const LoadingDots = styled.div`
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    
    span {
        animation: ${blink} 1.4s infinite both;
        &:nth-child(2) { animation-delay: .2s; }
        &:nth-child(3) { animation-delay: .4s; }
    }
`;

// 消息气泡样式
const MessageBubble = styled.div<{ role: 'self' | 'assistant'; isui?: boolean; isFullscreen?: boolean; messageId?: string }>`
    font-size: 14px;
    position: relative;
    max-width: ${props => props.isFullscreen ? '100%' : props.isui ? '95%' : '80%'};
    padding: 10px 15px;
    padding-bottom: 10px;
    border-radius: 15px;
    background-color: ${({ role }) => (role === 'self' ? '#007bff' : '#fff')};
    color: ${({ role }) => (role === 'self' ? '#fff' : '#333')};
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    word-wrap: break-word;
    width: ${props => props.isFullscreen ? 'auto' : props.isui ? '90%' : 'fit-content'};
    min-width: ${props => props.isFullscreen ? 'auto' : props.isui ? '90%' : 'auto'};
    display: inline-block;
    transition: all 0.3s ease;
    ${props => props.isFullscreen && `
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1000 !important;
        border-radius: 0;
        overflow-y: auto;
        margin: 0 !important;
    `}

    & pre {
        background-color: #f6f8fa;
        padding: 16px;
        border-radius: 6px;
        overflow-x: auto;
    }

    & code {
        background-color: rgba(0, 0, 0, 0.05);
        padding: 0.2em 0.4em;
        border-radius: 3px;
        font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    }

    & a {
        color: ${({ role }) => (role === 'self' ? '#fff' : '#007bff')};
        text-decoration: none;
        &:hover {
            text-decoration: underline;
        }
    }

    & p {
        margin: 0.5em 0;
        &:first-child {
            margin-top: 0;
        }
        &:last-child {
            margin-bottom: 0;
        }
    }

    & ol, & ul {
        margin: 0;
        padding-left: 20px;
        
        & li {
            margin: 0;
            padding: 0;
            line-height: 2em;
        }
    }
`;

// 全屏按钮样式
const FullscreenButton = styled(Button) <{ $isFullscreen?: boolean }>`
    position: ${props => props.$isFullscreen ? 'fixed' : 'absolute'} !important;
    top: 10px;
    right: 10px;
    background: transparent !important;
    border: none !important;
    color: rgba(0, 0, 0, 0.45) !important;
    box-shadow: none !important;
    z-index: ${props => props.$isFullscreen ? 1001 : 0};

    &:hover {
        color: rgba(0, 0, 0, 0.65) !important;
        background: transparent !important;
    }
`;

// 分享按钮样式
const ShareButton = styled(Button)<{ $isFullscreen?: boolean }>`
    position: ${props => props.$isFullscreen ? 'fixed' : 'absolute'} !important;
    bottom: ${props => props.$isFullscreen ? '20px' : '10px'};
    right: ${props => props.$isFullscreen ? '20px' : '10px'};
    background: ${props => props.$isFullscreen ? 'rgba(255, 255, 255, 0.9)' : 'transparent'} !important;
    border: none !important;
    color: #07C160 !important;
    box-shadow: ${props => props.$isFullscreen ? '0 2px 8px rgba(0, 0, 0, 0.15)' : 'none'} !important;
    padding: ${props => props.$isFullscreen ? '12px' : '4px 8px'};
    z-index: ${props => props.$isFullscreen ? 1001 : 0};
    font-size: ${props => props.$isFullscreen ? '20px' : '16px'};
    border-radius: ${props => props.$isFullscreen ? '50%' : '4px'};
    width: ${props => props.$isFullscreen ? '48px !important' : 'auto'};
    height: ${props => props.$isFullscreen ? '48px !important' : 'auto'};
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;

    &:hover {
        color: #06AE56 !important;
        background: ${props => props.$isFullscreen ? '#fff' : 'transparent'} !important;
        transform: ${props => props.$isFullscreen ? 'translateY(-2px)' : 'none'};
    }

    .anticon {
        font-size: ${props => props.$isFullscreen ? '24px' : '16px'};
    }
`;

// 问诊状态接口
interface ConsultationState {
    step: 'patient_list' | 'info' | 'fill' | 'succeed';
    patient?: Patient | null;
    recordId?: string;
}

const MessageContent: React.FC<MessageContentProps> = ({
    message,
    onSendMessage,
    onUIStateChange,
    initialUIState
}) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [consultationState, setConsultationState] = useState<ConsultationState>({
        step: initialUIState?.step || 'patient_list',
        patient: initialUIState?.patient,
        recordId: initialUIState?.recordId
    });
    const bubbleRef = useRef<HTMLDivElement>(null);
    const { role = 'assistant', type = 'text' } = message;

    // 滚动到当前消息
    const scrollToCurrentMessage = () => {
        setTimeout(() => {
            const messageElement = document.querySelector(`[message-id="${message.id}"]`);
            if (messageElement) {
                messageElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 200);
    };

    // 处理全屏切换
    const handleFullscreenToggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsFullscreen(!isFullscreen);
        if (isFullscreen) {
            scrollToCurrentMessage();
        }
    };

    // 处理状态变更
    const handleStateChange = useCallback((state: any) => {
        // 更新本地状态
        if (state.recordId) {
            setConsultationState(prev => ({
                ...prev,
                recordId: state.recordId
            }));
        }

        // 保存完整状态
        const fullState = {
            ...state,
            step: consultationState.step,
            patient: consultationState.patient
        };
        onUIStateChange?.(fullState);
    }, [consultationState.step, consultationState.patient, onUIStateChange]);

    // 处理分享
    const handleShare = (e: React.MouseEvent) => {
        e.stopPropagation();
        console.log('Share clicked');
        // TODO: 实现分享逻辑
    };

    // 渲染UI内容
    const renderUIContent = (uiMessage: UIMessage) => {
        if (uiMessage.ui_type === 'consultation_form') {
            switch (consultationState.step) {
                case 'patient_list':
                    return (
                        <PatientList
                            key={message.id}
                            {...uiMessage.ui_data}
                            isFullscreen={isFullscreen}
                            onNavigate={scrollToCurrentMessage}
                            onSelect={async (patient) => {
                                const newState = {
                                    step: 'info' as const,
                                    patient
                                };
                                setConsultationState(prev => ({
                                    ...prev,
                                    ...newState
                                }));
                                onUIStateChange?.({
                                    ...newState,
                                    recordId: consultationState.recordId
                                });
                                setTimeout(scrollToCurrentMessage, 100);
                            }}
                        />
                    );
                case 'info':
                    return (
                        <Info
                            {...consultationState.patient}
                            accountId={uiMessage.ui_data.accountId as string}
                            templateId={uiMessage.ui_data.templateId as string}
                            doctorId={uiMessage.ui_data.doctorId as string}
                            isFullscreen={isFullscreen}
                            onBack={() => {
                                const newState = {
                                    step: 'patient_list' as const,
                                    patient: undefined
                                };
                                setConsultationState(prev => ({
                                    ...prev,
                                    ...newState
                                }));
                                onUIStateChange?.({
                                    ...newState,
                                    recordId: consultationState.recordId
                                });
                            }}
                            onSubmit={async (values) => {
                                try {
                                    const newPatient: PatientInfoForm = {
                                        ...values,
                                        patientId: values.patientId || undefined,
                                        accountId: uiMessage.ui_data.accountId as string,
                                        templateId: uiMessage.ui_data.templateId as string,
                                        doctorId: uiMessage.ui_data.doctorId as string,
                                    };

                                    try {
                                        const response = await savePatientInfo(newPatient);
                                        if (!response?.data?.recordId) {
                                            console.error('保存患者信息返回数据缺少recordId:', response);
                                            antMessage.error('提交失败：返回数据格式错误');
                                            return;
                                        }

                                        const patientData: Patient = {
                                            patientId: newPatient.patientId || '',
                                            name: newPatient.name,
                                            gender: newPatient.gender === 'man' ? 'man' : 'woman',
                                            age: newPatient.age,
                                            relation: 'self',
                                            weight: newPatient.weight,
                                            height: newPatient.height,
                                            maritalStatus: newPatient.maritalStatus,
                                            contactWay: newPatient.contactWay,
                                            symptomDescription: newPatient.symptomDescription,
                                            supplementaryMaterials: newPatient.supplementaryMaterials ? [newPatient.supplementaryMaterials] : undefined
                                        };

                                        const newState = {
                                            step: 'fill' as const,
                                            patient: patientData,
                                            recordId: response.data.recordId
                                        };
                                        setConsultationState(newState);
                                        onUIStateChange?.(newState);

                                        setTimeout(scrollToCurrentMessage, 100);
                                    } catch (error) {
                                        antMessage.error('提交失败');
                                        console.error('保存患者信息失败:', error);
                                    }
                                } catch (error) {
                                    antMessage.error('提交失败');
                                    console.error('提交问诊基本信息失败:', error);
                                }
                            }}
                        />
                    );
                case 'fill':
                    return (
                        <Fill
                            templateId={uiMessage.ui_data.templateId as string}
                            recordId={consultationState.recordId!}
                            onSubmit={() => {
                                const newState = {
                                    step: 'succeed' as const,
                                    patient: consultationState.patient,
                                    recordId: consultationState.recordId
                                };
                                setConsultationState(newState);
                                onUIStateChange?.(newState);
                            }}
                            onScrollToCurrentMessage={scrollToCurrentMessage}
                            isFullscreen={isFullscreen}
                            onSendMessage={onSendMessage}
                            messageId={uiMessage.id}
                            initialState={initialUIState}
                            onStateChange={handleStateChange}
                        />
                    );
                case 'succeed':
                    return <Succeed />;
                default:
                    return null;
            }
        } else if (uiMessage.ui_type === 'knowledge_item_summary') {
            const { name, medicineInfoList } = uiMessage.ui_data;

            return (
                <KnowledgeItemSummary
                    name={name}
                    medicineInfoList={medicineInfoList}
                    isFullscreen={isFullscreen}
                    contentType={uiMessage.ui_data["content_type"]}
                    contentID={uiMessage.ui_data["content_id"]}
                />
            );
        }
        return null;
    };

    // 渲染消息内容
    const renderContent = () => {
        switch (type) {
            case 'loading':
                return (
                    <LoadingDots>
                        <span>.</span>
                        <span>.</span>
                        <span>.</span>
                    </LoadingDots>
                );
            case 'text':
            case 'markdown':
                return (
                    <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw, rehypeHighlight]}
                    >
                        {message.text}
                    </ReactMarkdown>
                );
            case 'ui':
                return renderUIContent(message as UIMessage);
            default:
                return message.text;
        }
    };

    return (
        <MessageBubble
            ref={bubbleRef}
            role={role}
            isui={type === 'ui'}
            isFullscreen={isFullscreen}
            message-id={message.id}
        >
            {type === 'ui' && (
                <>
                    <FullscreenButton
                        $isFullscreen={isFullscreen}
                        icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                        onClick={handleFullscreenToggle}
                    />
                    {(message as UIMessage).ui_type === 'knowledge_item_summary' && (
                        <ShareButton
                            $isFullscreen={isFullscreen}
                            icon={<WechatOutlined />}
                            onClick={handleShare}
                        />
                    )}
                </>
            )}
            {renderContent()}
        </MessageBubble>
    );
};

export default MessageContent; 